import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva } from "class-variance-authority";
import { cn } from "../../../client/cn";

type SwitchTone = "primary" | "neutral";

export interface SwitchProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    "onCheckedChange" | "className"
  > {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  tone?: SwitchTone;
  className?: string;
  containerClassName?: string;
  thumbClassName?: string;
  label?: string;
  description?: string;
}

const switchTrackVariants = cva(
  "inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-boxdark",
  {
    variants: {
      tone: {
        primary:
          "focus-visible:ring-primary/40 data-[state=checked]:bg-primary data-[state=unchecked]:bg-neutral-300 dark:data-[state=unchecked]:bg-neutral-600",
        neutral:
          "focus-visible:ring-neutral/40 data-[state=checked]:bg-neutral-700 data-[state=unchecked]:bg-neutral-300 dark:data-[state=checked]:bg-neutral-100 dark:data-[state=unchecked]:bg-neutral-600",
      },
    },
    defaultVariants: {
      tone: "primary",
    },
  },
);

const switchThumbVariants = cva(
  "pointer-events-none block h-5 w-5 rounded-full bg-white shadow-xs ring-0 transition-transform duration-200 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
);

export function Switch({
  checked,
  onCheckedChange,
  tone = "primary",
  className = "",
  containerClassName = "",
  thumbClassName = "",
  label,
  description,
  disabled,
  ...props
}: SwitchProps) {
  const switchControl = (
    <SwitchPrimitive.Root
      {...props}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className={cn(switchTrackVariants({ tone }), className)}
    >
      <SwitchPrimitive.Thumb
        className={cn(switchThumbVariants(), thumbClassName)}
      />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) {
    return switchControl;
  }

  return (
    <label
      className={cn(
        "inline-flex items-center gap-3 text-sm text-black dark:text-white",
        disabled && "cursor-not-allowed opacity-60",
        containerClassName,
      )}
    >
      <span className="flex flex-col gap-0.5">
        {label && <span className="font-medium">{label}</span>}
        {description && (
          <span className="text-xs text-neutral-600 dark:text-neutral-400">
            {description}
          </span>
        )}
      </span>
      {switchControl}
    </label>
  );
}
